import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  TextField,
  Typography,
  Button,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  PaginationItem,
  Tooltip,
  TableSortLabel,
} from "@mui/material";
import { debounce } from "lodash";
import * as XLSX from "xlsx";
import {
  NoDataFoundRow,
  StyledTableCell,
  StyledTableRow,
} from "../../../components/common/StyledTableComponents";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../../components/common/Loader/Loader";
import RefreshIcon from "@mui/icons-material/Refresh";
import CustomSelect1 from "../../../components/common/CustomSelect1";
import {
  getHiredCandidatesReportRecruiterApi,
  RecruterEarning,
} from "../../../features/Recruiter/RecruiterReports/Reports";
import { MEDIA_URL } from "../../../api/endpoints";
import NormalDatePicker from "../../../components/common/NormalDatePicker";
import { CustomButton } from "../../../components/common/LoadingButton";
import StatusChip from "../../../components/common/StatusChip/StatusChip";
import UpdateCandidateStatusButton from "./UpdateCandidateStatusButton";
import CustomSearchField from "../../../components/common/CustomSearchField";
import ExportToExcelButton from "../../../components/common/ExportToExcelButton";
import {
  formatAmount,
  formatDate1,
  formatDate2,
  formatSearchDateTime,
  formatUSAmount,
} from "../../../utils/helpers";
import {
  filterReportsName,
  formatDateToYearMonth,
  getHeadersList,
  initialEndDate,
  initialMonth,
  initialStartDate,
} from "./RecruterReports";
import CustomPagination from "../../../components/common/CustomPagination";
import MonthDatePicker from "../../../components/common/MonthDatePicker";
import {
  renderLastThreeIndustry,
  renderLastThreeIndustryAsString,
  renderLastThreeJobs,
  renderLastThreeJobsAsString,
} from "../../../components/common/RenderLastThreeJobs";

let options = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "engaged", label: "Engaged" },
  { value: "interview", label: "Interview" },
  { value: "offer extended", label: "Offer Extended" },
  { value: "hired", label: "Hired" },
];
const capitalize = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};
const CandidateStatusRecruterReport = ({
  RecruiterId = null,
  activeButton,
  candidate_list = false,
  rowsPerPage,
  setRowsPerPage,
  totalRecords,
  setTotalRecords,
}) => {
  const headers = useMemo(() => getHeadersList(activeButton), [activeButton]);
  const isActive = useMemo(
    () => filterReportsName(activeButton),
    [activeButton]
  );
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [selectedOptions, setSelectedOption] = useState("all");
  const [resetFlag, setResetFlag] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [Count, setCount] = useState(1);
  const [data, setData] = useState([]);
  const [fromDate, setFromDate] = useState(initialStartDate);
  const [toDate, setToDate] = useState(initialEndDate);
  const [hiredMonth, setHiredMonth] = useState(initialMonth);
  const handleHiredMonthChange = (newValue) => setHiredMonth(newValue);

  const today = new Date();
  const handleStartDateChange = (date) => {
    setFromDate(date);
    if (toDate && date > toDate) {
      setToDate(null);
    }
  };
  const handleEndDateChange = (date) => setToDate(date);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getCandidatesReportsForClient = async (
    query = searchQuery,
    pageNum = 1,
    startDate = fromDate,
    endDate = toDate,
    selectedstatus = selectedOptions,
    pagesize = rowsPerPage,
    startMonth = hiredMonth,
    isActivePage = isActive,
    isExport = false
  ) => {
    setLoading(true);
    let data = {
      ...(isActivePage
        ? {
            status: candidate_list ? "both" : selectedstatus,
            fromDate: formatDate1(startDate),
            toDate: formatDate1(endDate),
          }
        : { hiredmonth: formatDateToYearMonth(startMonth) }),

      id: RecruiterId,
      pagenumber: pageNum,
      searchitem: query,
      pagesize: pagesize,
      export: isExport,
    };

    try {
      const { payload } = await dispatch(
        isActivePage
          ? getHiredCandidatesReportRecruiterApi(data)
          : RecruterEarning(data)
      );
      if (payload.status === 200) {
        if (isExport) {
          exportToExcel(payload.data || [], isActivePage);
        } else {
          setData(payload.data);
          setCount(payload.noOfPages);
          setPage(pageNum);
          setTotalRecords(payload.totalRecords);
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  const debouncedSearch = useCallback(
    debounce(
      (
        query,
        page,
        updatedFromDate,
        updatedToDate,
        selectedOptions,
        rowsPerPage,
        hiredMonth,
        isActive
      ) => {
        getCandidatesReportsForClient(
          query,
          page,
          updatedFromDate,
          updatedToDate,
          selectedOptions,
          rowsPerPage,
          hiredMonth,
          isActive
        );
      },
      300
    ),
    []
  );
  const handleSearchInputChange = (e) => {
    setSearchQuery(e.target.value);
    debouncedSearch(
      e.target.value,
      1,
      fromDate,
      toDate,
      selectedOptions,
      rowsPerPage,
      hiredMonth,
      isActive
    );
  };
  const handleReset = async () => {
    setFromDate(initialStartDate);
    setToDate(initialEndDate);
    setHiredMonth(initialMonth);
    setSelectedOption("all");
    setResetFlag(true);
    setSearchQuery("");
    setRowsPerPage(10);
  };
  useEffect(() => {
    if (resetFlag) {
      getCandidatesReportsForClient().then(() => setResetFlag(false));
    }
  }, [resetFlag]);

  const handlePageChange = (value) => {
    setPage(value);
    getCandidatesReportsForClient(searchQuery, value);
  };
  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
    getCandidatesReportsForClient(
      searchQuery,
      1,
      fromDate,
      toDate,
      selectedOptions,
      newRowsPerPage
    );
  };

  const GetRecruiterReportsExport = async () => {
    await getCandidatesReportsForClient(
      searchQuery,
      page,
      fromDate,
      toDate,
      selectedOptions,
      rowsPerPage,
      hiredMonth,
      isActive,
      true
    );
  };
  const exportToExcel = (data, isActivePage) => {
    const formattedData = data.map((row, index) => {
      const paymentDate = formatDate1(row.recruiter_paymentDate?.$date);
      const HireDate = formatDate1(row.candidate_hiredate.$date);
      const rowData = {
        No: index + 1,
        "Enroll Date": formatDate2(row.candidate_cdate.$date) || "N/A",
        "Candidate Name": `${row.candidate_firstname} ${row.candidate_lastname}`,
        Email: row.candidate_email,
        "Contact No.": row.candidate_contactno,
        "Job Title": renderLastThreeJobsAsString(row.candidate_company),
        "Job Industry/Sector": renderLastThreeIndustryAsString(
          row.candidate_company
        ),
        "No. of Year Experiance": row.candidate_experiance,
        "Geographic Preference":
          row.candidate_anylocation === "any"
            ? "Any Location"
            : row.candidate_specifiedlocation
                ?.map((label) => label)
                ?.join(", "),
        "Language Proficiency": row.candidate_language_reference
          ?.map((lang) => lang.label)
          ?.join(", "),
        ...(isActivePage
          ? {
              "Resume Link": {
                t: "s",
                v: "View Resume",
                l: { Target: `${MEDIA_URL}${row.candidate_resumeurl}` },
              },
              "Last Modified Date-Time": formatSearchDateTime(
                row.candidate_mdate
              ),
              Status: capitalize(row.candidate_status),
            }
          : {
              "Hired Date": HireDate,
              "Payment Date": paymentDate,
              "Client Name": row.client_name,
              "Commission Earned": row.is_recruiter_paid
                ? `${formatUSAmount(row.recruiter_percentage)}`
                : "-",
            }),
        "Compensation Range ( Base Salary + Bonus In cash / year)": `$${formatAmount(
          row.candidate_compensation_from
        )} to $${formatAmount(row.candidate_compensation_to)}`,
      };
      return rowData;
    });
    const worksheet = XLSX.utils.json_to_sheet(formattedData);

    const headers = Object.keys(formattedData[0]);
    const colWidths = headers.map((header) => {
      const maxContentLength = formattedData.reduce((max, row) => {
        const cellContent = row[header] ? row[header].toString() : "";
        return Math.max(max, cellContent.length);
      }, header.length);

      return { wch: maxContentLength + 2 }; // Adding padding for better spacing
    });

    worksheet["!cols"] = colWidths;

    const workbook = XLSX.utils.book_new();
    let reportName = isActivePage ? "Candidate Status" : "Recruiter Earning";
    XLSX.utils.book_append_sheet(workbook, worksheet, `${reportName} Report`);
    XLSX.writeFile(workbook, `${reportName} Report.xlsx`);
  };
  useEffect(() => {
    getCandidatesReportsForClient();
    handleReset();
  }, [activeButton]);
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;
    return [...data].sort((a, b) => {
      let aValue, bValue;
      if (sortConfig.key === "candidate name") {
        aValue =
          `${a.candidate_firstname} ${a.candidate_firstname}`.toLowerCase();
        bValue =
          `${b.candidate_firstname} ${b.candidate_firstname}`.toLowerCase();
      } else if (sortConfig.key === "enroll date") {
        aValue = new Date(a.candidate_cdate.$date);
        bValue = new Date(b.candidate_cdate.$date);
      } else {
        return 0;
      }
      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig]);
  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#fff",
          p: 3,
          borderRadius: "10px",
        }}
      >
        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <Grid item xs={12} md={8} container spacing={2} alignItems="center">
            {isActive ? (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <NormalDatePicker
                    label="From date"
                    name="from_date"
                    selected={fromDate}
                    onChange={handleStartDateChange}
                    maxDate={today}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <NormalDatePicker
                    label="To date"
                    name="To_date"
                    selected={toDate}
                    onChange={handleEndDateChange}
                    maxDate={today}
                    minDate={fromDate}
                  />
                </Grid>
                {!candidate_list && (
                  <Grid item xs={12} sm={6} md={3}>
                    <CustomSelect1
                      options={options}
                      state={selectedOptions}
                      setState={setSelectedOption}
                      id="status"
                      label={"Select Status"}
                      sx={{
                        minWidth: { xs: "100%" },
                        height: "40px",
                      }}
                    />{" "}
                  </Grid>
                )}
              </>
            ) : (
              <Grid item xs={12} sm={6} md={3}>
                <MonthDatePicker
                  label="Payment Month"
                  value={hiredMonth}
                  onChange={handleHiredMonthChange}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <CustomButton
                  variant="contained"
                  color="primary"
                  onClick={() => getCandidatesReportsForClient(searchQuery)}
                  sx={{
                    mr: 2,
                  }}
                >
                  Apply
                </CustomButton>
                <Tooltip title="Refresh" arrow>
                  <CustomButton
                    color="error"
                    size="small"
                    onClick={handleReset}
                  >
                    <RefreshIcon />
                  </CustomButton>{" "}
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
          <Grid container justifyContent="flex-end" item xs={12} md={4}>
            <Grid item xs={8}>
              <CustomSearchField
                searchQuery={searchQuery}
                onChange={handleSearchInputChange}
              />
            </Grid>
            <Grid item xs={4}>
              <ExportToExcelButton onClick={GetRecruiterReportsExport} />
            </Grid>
          </Grid>
        </Grid>
      </Box>
      {loading ? (
        <Loader />
      ) : (
        <>
          <TableContainer component={Paper} sx={{ mt: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  {headers?.map((header, index) => {
                    const isSortable =
                      header === "Candidate Name" || header === "Enroll Date";
                    const sortKey = header.toLowerCase();
                    return (
                      <StyledTableCell key={index}>
                        {isSortable ? (
                          <TableSortLabel
                            active={sortConfig.key === sortKey}
                            direction={sortConfig.direction}
                            onClick={() => handleSort(sortKey)}
                          >
                            {header}
                          </TableSortLabel>
                        ) : (
                          header
                        )}
                      </StyledTableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData && sortedData.length > 0 ? (
                  <>
                    {sortedData.map((row, index) => {
                      const CDate = formatDate2(row.candidate_cdate.$date);
                      const dateTime = formatSearchDateTime(
                        row.candidate_mdate
                      );
                      const paymentDate = formatDate1(
                        row.recruiter_paymentDate?.$date
                      );
                      const HireDate = formatDate1(
                        row.candidate_hiredate.$date
                      );
                      return (
                        <TableRow
                          key={index}
                          sx={{
                            "&:nth-of-type(odd)": {
                              backgroundColor: "#f5f5f5",
                            },
                            fontSize: "12px",
                          }}
                        >
                          <StyledTableRow sx={{ textAlign: "center" }}>
                            {(page - 1) * rowsPerPage + index + 1}.
                          </StyledTableRow>
                          <StyledTableRow>{CDate}</StyledTableRow>
                          <StyledTableRow>
                            {row.candidate_firstname} {row.candidate_lastname}
                          </StyledTableRow>
                          <StyledTableRow>{row.candidate_email}</StyledTableRow>
                          <StyledTableRow sx={{ textWrap: "nowrap" }}>
                            {row.candidate_contactno}
                          </StyledTableRow>
                         
                          <StyledTableRow>
                            {renderLastThreeJobs(row.candidate_company)}
                          </StyledTableRow>
                          <StyledTableRow>
                            {renderLastThreeIndustry(row.candidate_company)}
                          </StyledTableRow>
                          <StyledTableRow>
                            {row.candidate_experiance}
                          </StyledTableRow>
                          <StyledTableRow>
                            {row.candidate_anylocation === "any"
                              ? "Any Location"
                              : Array.isArray(
                                  row.candidate_specifiedlocation
                                ) &&
                                row.candidate_specifiedlocation?.map(
                                  (label, idx) => (
                                    <React.Fragment key={idx}>
                                      {label}
                                      {idx <
                                        row.candidate_specifiedlocation
                                          ?.length -
                                          1 && <br />}
                                    </React.Fragment>
                                  )
                                )}
                          </StyledTableRow>
                          <StyledTableRow>
                            {row.candidate_language_reference?.map(
                              (lang, index) => {
                                return (
                                  <React.Fragment key={index}>
                                    {lang.label}
                                    {row.candidate_language_reference?.length -
                                      1 >
                                      index && <span>, </span>}
                                  </React.Fragment>
                                );
                              }
                            )}
                          </StyledTableRow>
                          <StyledTableRow align="right">{`${formatUSAmount(
                            row.candidate_compensation_from
                          )} - ${formatUSAmount(
                            row.candidate_compensation_to
                          )}`}</StyledTableRow>
                          {isActive ? (
                            <>
                              <StyledTableRow>
                                <a
                                  href={`${MEDIA_URL}${row.candidate_resumeurl}`}
                                  target="_blank" // Opens the link in a new tab
                                  rel="noopener noreferrer" // Provides security for external links
                                  style={{
                                    color: "#0075FF",
                                    textDecoration: "none",
                                  }} // Custom styling for the link
                                >
                                  View Resume
                                </a>
                              </StyledTableRow>
                              <StyledTableRow>{dateTime}</StyledTableRow>
                              <StyledTableRow
                                sx={{ textTransform: "capitalize" }}
                              >
                                {candidate_list ? (
                                  <UpdateCandidateStatusButton
                                    row={row}
                                    page={page}
                                    getHiredCandidatesReportClient={
                                      getCandidatesReportsForClient
                                    }
                                  />
                                ) : (
                                  <StatusChip status={row.candidate_status} />
                                )}
                              </StyledTableRow>
                            </>
                          ) : (
                            <>
                              <StyledTableRow>{HireDate}</StyledTableRow>
                              <StyledTableRow>{paymentDate}</StyledTableRow>
                              <StyledTableRow>{row.client_name}</StyledTableRow>
                              <StyledTableRow sx={{ textAlign: "end" }}>
                                {row.is_recruiter_paid
                                  ? `${formatUSAmount(
                                      row.recruiter_percentage
                                    )}`
                                  : "-"}
                              </StyledTableRow>
                            </>
                          )}
                        </TableRow>
                      );
                    })}
                  </>
                ) : (
                  <>
                    <NoDataFoundRow
                      colSpan={9}
                      message="Candidates are not available!"
                    />
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {sortedData?.length > 0 && (
            <div>
              <CustomPagination
                totalRecords={totalRecords}
                rowsPerPageOptions={[10, 25, 50, 100]}
                initialRowsPerPage={rowsPerPage}
                setRowsPerPage1={setRowsPerPage}
                initialPage={page - 1}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </div>
          )}
        </>
      )}
    </>
  );
};

export default CandidateStatusRecruterReport;
